import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

interface GlassContainerProps {
  children?: React.ReactNode;
  className?: string;
  hoverEffect?: boolean;
  onClick?: () => void;
}

export const GlassContainer: React.FC<GlassContainerProps> = ({ children, className = '', hoverEffect = false, onClick }) => (
  <div
    onClick={onClick}
    className={`
      bg-white/60 backdrop-blur-2xl border border-white/50 shadow-xl
      rounded-3xl relative overflow-hidden
      ${hoverEffect ? 'transition-all duration-500 hover:bg-white/70 hover:scale-[1.02] hover:shadow-2xl hover:shadow-brand-lime/10' : ''}
      ${className}
    `}>
    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/40 to-transparent pointer-events-none" />
    {children}
  </div>
);

export const GradientText = ({ children, className = '' }: { children?: React.ReactNode, className?: string }) => (
  <span className={`bg-clip-text text-transparent bg-gradient-to-r from-brand-lime to-brand-yellow ${className}`}>
    {children}
  </span>
);

export const PrimaryButton = ({ onClick, children, className = '', fullWidth = false }: { onClick?: (e?: any) => void, children?: React.ReactNode, className?: string, fullWidth?: boolean }) => (
  <button
    onClick={onClick}
    className={`
      relative group overflow-hidden px-6 py-3 md:px-8 md:py-4 rounded-full font-bold text-white shadow-lg shadow-brand-lime/30 transition-all duration-300 hover:shadow-brand-lime/50 hover:-translate-y-1
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-brand-lime to-brand-yellow transition-transform duration-300 group-hover:scale-105" />
    <span className="relative z-10 flex items-center justify-center gap-2 text-sm md:text-base">{children}</span>
  </button>
);

export const ShineButton = ({ onClick, children, className = '' }: { onClick?: () => void, children?: React.ReactNode, className?: string }) => (
  <button
    onClick={onClick}
    className={`
      relative px-6 py-3 md:px-8 md:py-4 rounded-full font-bold text-white overflow-hidden shadow-2xl shadow-brand-lime/40
      bg-gradient-to-r from-brand-lime to-brand-yellow
      transition-all duration-300 hover:-translate-y-1 hover:shadow-brand-lime/60
      ${className}
    `}
  >
    <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent,25%,rgba(255,255,255,0.3),50%,transparent)] bg-[length:200%_100%] animate-shimmer" />
    <span className="relative z-10 flex items-center justify-center gap-2 text-sm md:text-base">{children}</span>
  </button>
);

export const OutlineButton = ({ onClick, children, className = '' }: { onClick?: () => void, children?: React.ReactNode, className?: string }) => (
  <button
    onClick={onClick}
    className={`
      px-6 py-3 md:px-8 md:py-4 rounded-full font-bold text-gray-700 bg-white/50 border-2 border-white/80 backdrop-blur-sm
      transition-all duration-300 hover:border-brand-lime hover:text-brand-lime hover:bg-white
      text-sm md:text-base
      ${className}
    `}
  >
    {children}
  </button>
);

export const Badge = ({ children, className = '' }: { children?: React.ReactNode, className?: string }) => (
  <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-brand-lime/10 to-brand-yellow/10 text-gray-800 border border-brand-lime/20 mb-4 ${className}`}>
    {children}
  </span>
);

export const Section = ({ children, className = '', id = '' }: { children?: React.ReactNode, className?: string, id?: string }) => (
  <section id={id} className={`py-32 px-6 relative z-10 ${className}`}>
    <div className="max-w-7xl mx-auto">
      {children}
    </div>
  </section>
);

export const SectionHeader = ({ badge, title, subtitle }: { badge?: string, title: React.ReactNode, subtitle?: string }) => (
  <div className="text-center mb-20 max-w-3xl mx-auto">
    {badge && <Badge>{badge}</Badge>}
    <h2 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-gray-900">{title}</h2>
    {subtitle && <p className="text-xl text-gray-500 leading-relaxed">{subtitle}</p>}
  </div>
);

export const Marquee = ({ children, direction = 'left' }: { children?: React.ReactNode, direction?: 'left' | 'right' }) => (
  <div className="flex overflow-hidden w-full mask-gradient select-none">
    <div className={`
      flex gap-12 py-6 w-max will-change-transform
      ${direction === 'left' ? 'animate-scroll-left' : 'animate-scroll-right'}
      hover:[animation-play-state:paused]
    `}>
      <div className="flex gap-12 items-center">
        {children}
      </div>
      <div className="flex gap-12 items-center">
        {children}
      </div>
    </div>
  </div>
);

export const Accordion = ({ items }: { items: { title: string, content: string }[] }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-4">
      {items.map((item, idx) => (
        <GlassContainer key={idx} className={`transition-all duration-300 ${openIndex === idx ? 'bg-white/80' : 'bg-white/40'}`}>
          <button
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            aria-expanded={openIndex === idx}
            aria-label={`${item.title} accordion toggle`}
            className="w-full px-8 py-6 flex justify-between items-center text-left"
          >
            <span className="font-bold text-lg text-gray-900">{item.title}</span>
            {openIndex === idx ? <ChevronUp className="text-brand-lime" /> : <ChevronDown className="text-gray-400" />}
          </button>
          <div className={`px-8 overflow-hidden transition-all duration-300 ${openIndex === idx ? 'max-h-96 pb-8 opacity-100' : 'max-h-0 opacity-0'}`}>
            <p className="text-gray-500 leading-relaxed">{item.content}</p>
          </div>
        </GlassContainer>
      ))}
    </div>
  );
};

export const SplitText = ({ children, className = '', delay = 0 }: { children?: string, className?: string, delay?: number }) => {
  if (!children) return null;
  return (
    <span className={`${className}`}>
      {children.split('').map((char, i) => (
        <span
          key={i}
          className="inline-block opacity-0 animate-text-reveal origin-bottom transform-gpu"
          style={{
            animationDelay: `${delay + (i * 0.04)}s`,
            whiteSpace: char === ' ' ? 'pre' : 'normal'
          }}
        >
          {char}
        </span>
      ))}
    </span>
  );
};

// Export Breadcrumb component
export { Breadcrumb, generateBreadcrumbs } from './Breadcrumb';
export type { BreadcrumbItem } from './Breadcrumb';
export { OptimizedImage, OptimizedAvatar } from './OptimizedImage';
